import { Component, ElementRef, ViewChild, OnInit, OnChanges, OnDestroy, ViewEncapsulation, EventEmitter, Input, Output, SimpleChanges } from '@angular/core';
import { Subject, timer, of, debounceTime, map, switchMap, takeUntil } from 'rxjs';
import { Gantt, GanttConfigOptions as DhtmlxGanttConfigOptions, GanttStatic, Link, Task } from '../../../../assets/js/codebase/dhtmlxgantt';
import { DataTaskService } from '@app/core/services/data-task.service';
import { StateService } from '@app/core/services/state.service';
import { ModalService } from '@app/core/services/modal.service';
import { PopupService } from '@app/core/services/popup.service';
import { RememberService } from '@app/core/services/remember.service';
import { AppSettingsService } from '@app/core/services/app-settings.service';
import { createDefaultGanttOptions, customCoerceBooleanProperty, mergeGanttOptions, normalizeGanttOptions, toNativeGanttConfig } from './gantt-adapter';
import { GanttColumn, GanttOptions, GanttRememberState, GanttSetup, GanttTask, GanttTaskChangeInfo, MachineResource, PartialGanttOptions } from './gantt.types';
import { configureGanttZoom, getCurrentGanttZoomLevel, normalizeGanttZoomLevel } from './gantt-zoom';
import { applyRememberedColumnState, createGanttRememberState, createRememberedColumnState, getColumnPropertyKey, isGanttRememberState } from './gantt-remember';

@Component({
    selector: 'tt-gantt',
    templateUrl: './gantt.component.html',
    styleUrls: ['./gantt.component.css'],
    encapsulation: ViewEncapsulation.None,
})
export class GanttComponent implements OnInit, OnChanges, OnDestroy {
    @ViewChild('gantt_here', { static: true })
    public ganttContainer!: ElementRef<HTMLElement>;

    @Output()
    public ttReady = new EventEmitter<GanttStatic>();

    @Input()
    public ttRememberId = '';

    @Input()
    public ttOptions?: PartialGanttOptions;

    @Output()
    public ttOptionsChange = new EventEmitter<PartialGanttOptions>();

    private criticalPath = false;
    private multiSelect = false;
    private linksEditable = false;
    private resourceEnabled = false;

    private gantt?: GanttStatic;

    private readonly destroy$ = new Subject<void>();
    private readonly save$ = new Subject<void>();
    private readonly rememberState$ = new Subject<void>();
    private readonly taskClick$ = new Subject<string | number>();
    private readonly taskDblClick$ = new Subject<string | number>();

    private highlightedTaskIds = new Set<string | number>();
    private highlightedLinkIds = new Set<string | number>();
    private selectedPathRoot: string | number | null = null;

    private changes: Record<string, GanttTaskChangeInfo> = {};
    private backendOptions?: PartialGanttOptions;
    private rememberedState?: GanttRememberState;
    private resources: MachineResource[] = [];
    private lastRememberedStateJson = '';
    private lastNativeColumnSignature = '';
    private applyingRememberedState = false;

    public options: GanttOptions = createDefaultGanttOptions();

    constructor(
        private datatask: DataTaskService,
        private state: StateService,
        private modal: ModalService,
        private popup: PopupService,
        private remember: RememberService,
        private appSettings: AppSettingsService,
    ) {
        this.configureRxStreams();
    }

    async ngOnInit() {
        const response = await this.getGanttConfiguration();
        this.backendOptions = normalizeGanttOptions(response.options);

        this.resolveOptions();

        const tasks = this.normalizeTaskDates(response.data ?? []);
        const links = response.links ?? [];
        const resources = this.resolveResources(response.resources, tasks);
        this.resources = resources;
        this.enableResourcePanelForAvailableResources(resources);
        const assignedTasks = this.normalizeResourceBackedTaskOwners(tasks, resources);
        if (!this.options.columns.length) {
            this.options.columns = this.inferColumnsFromTasks(assignedTasks);
        }

        this.applyRememberedState(this.getLastRememberedStateLocal());

        const gantt = Gantt.getGanttInstance();
        this.gantt = gantt;

        this.configurePlugins(gantt);
        this.configureLocale(gantt);
        this.configureZooming(gantt);
        this.configureColumns(gantt);
        this.configureTemplates(gantt);
        this.configureEvents(gantt);
        this.applyNativeConfig(gantt);
        this.configureResourceConfig(gantt);
        this.configureLayout(gantt);
        this.configureAutoSchedulingProjectDates(gantt, assignedTasks);
        this.configureEditing(gantt);

        gantt.init(this.ganttContainer.nativeElement);

        gantt.parse({
            data: assignedTasks,
            links,
            resources,
        });
        this.syncResourceStore(gantt);

        this.restoreRememberedTimelinePosition(gantt, this.rememberedState?.timelineCenterDate);

        this.getLastRememberedState().then((state) => {
            this.applyRememberedState(state, true);

            if (state) {
                this.rememberCurrentStateLocal(state);
            }
        });
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (!this.gantt || !changes['ttOptions']) return;

        const previousColumnsSignature = this.getConfiguredColumnsSignature(this.options.columns);
        const previousResourceEnabled = this.options.resources.enabled;
        const previousResourceColumnsSignature = this.getConfiguredColumnsSignature(this.options.resources.columns);

        this.resolveOptions(true);
        this.applyRememberedState(this.rememberedState);

        const columnsChanged = previousColumnsSignature !== this.getConfiguredColumnsSignature(this.options.columns);
        const resourceLayoutChanged = previousResourceEnabled !== this.options.resources.enabled || previousResourceColumnsSignature !== this.getConfiguredColumnsSignature(this.options.resources.columns);

        if (columnsChanged) {
            this.configureColumns(this.gantt);
        }

        this.applyNativeConfig(this.gantt);
        this.configureResourceConfig(this.gantt);

        if (resourceLayoutChanged || columnsChanged) {
            this.configureLayout(this.gantt);
        }

        this.configureAutoSchedulingProjectDates(this.gantt);
        this.configureEditing(this.gantt);

        if (resourceLayoutChanged || columnsChanged) {
            this.gantt.resetLayout();
            this.syncResourceStore(this.gantt);
            this.gantt.refreshData();
        }

        this.gantt.render();
    }

    ngOnDestroy() {
        this.destroy$.next();
        this.destroy$.complete();

        if (this.gantt) {
            this.rememberCurrentState();
            this.gantt.destructor();
        }
    }

    private configureRxStreams(): void {
        this.save$
            .pipe(
                debounceTime(150),
                switchMap(() => of(this.save())),
                takeUntil(this.destroy$),
            )
            .subscribe();

        this.rememberState$.pipe(debounceTime(1000), takeUntil(this.destroy$)).subscribe(() => this.rememberCurrentState());

        this.taskClick$
            .pipe(
                switchMap((id) =>
                    timer(250).pipe(
                        takeUntil(this.taskDblClick$),
                        map(() => id),
                    ),
                ),
                takeUntil(this.destroy$),
            )
            .subscribe((id) => {
                this.highlightDependencyPath(id);
            });
    }

    private configurePlugins(gantt: GanttStatic): void {
        gantt.plugins({ multiselect: true });
        gantt.plugins({ critical_path: true });
        gantt.plugins({ auto_scheduling: true });
        gantt.plugins({ resources: true } as any);

        gantt.config.multiselect = this.multiSelect;
        gantt.config.highlight_critical_path = this.criticalPath;
    }

    private configureLocale(gantt: GanttStatic): void {
        gantt.i18n.setLocale('no');

        gantt.config.date_format = '%Y-%m-%d %H:%i:%s';
        gantt.config.auto_types = true;
        gantt.config.duration_unit = 'hour';
        gantt.config.time_step = 15;

        gantt.config['scale_unit'] = 'day';
        gantt.config['date_scale'] = '%d %M';
        gantt.config['subscales'] = [{ unit: 'hour', step: 4, date: '%H:%i' }];
    }

    private configureZooming(gantt: GanttStatic): void {
        configureGanttZoom(gantt, this.options.zoom, this.rememberedState?.zoomLevel);

        gantt.ext.zoom.attachEvent('onAfterZoom', () => {
            if (!this.applyingRememberedState) {
                this.rememberState$.next();
            }
        });
    }

    private configureEditing(gantt: GanttStatic): void {
        const tasksEditable = this.options.editing.tasks;
        const drag = this.options.editing.drag;
        const dragLinks = this.resolveDragOption(drag.links, this.linksEditable);
        const dragMove = this.resolveDragOption(drag.move, tasksEditable);
        const dragResize = this.resolveDragOption(drag.resize, tasksEditable);
        const dragProgress = this.resolveDragOption(drag.progress, tasksEditable);

        gantt.config.readonly = false; // don't use global readonly unless everything should lock

        gantt.config.drag_links = dragLinks;
        gantt.config.drag_move = dragMove;
        gantt.config.drag_resize = dragResize;
        gantt.config.drag_progress = dragProgress;

        if (drag.multiple !== null && drag.multiple !== undefined) {
            gantt.config.drag_multiple = this.resolveDragOption(drag.multiple, false);
        }

        if (drag.project !== null && drag.project !== undefined) {
            gantt.config.drag_project = this.resolveDragOption(drag.project, false);
        }

        if (drag.lightbox !== null && drag.lightbox !== undefined) {
            gantt.config.drag_lightbox = this.resolveDragOption(drag.lightbox, false);
        }

        this.configureResourceConfig(gantt);
    }

    private resolveDragOption(value: unknown, fallback: boolean): boolean {
        return value === null || value === undefined ? fallback : customCoerceBooleanProperty(value as any);
    }

    private configureResourceConfig(gantt: GanttStatic): void {
        gantt.config.resources = this.getGanttResourcesConfig();
        gantt.config.resource_store = 'resource';
        gantt.config.resource_property = 'owner';
        gantt.config.resource_render_empty_cells = true;
        (gantt as any).resource_table = {
            ...(gantt as any).resource_table,
            columns: this.getNativeResourceColumns(),
            row_height: gantt.config.row_height,
        };
    }

    private configureColumns(gantt: GanttStatic): void {
        gantt.config.columns = this.options.columns.filter((column) => !this.isColumnHidden(column)).map((column) => this.toGanttGridColumn(column));
    }

    private configureLayout(gantt: GanttStatic): void {
        const nativeResourceColumns = this.getNativeResourceColumns();
        const resourcesEnabled = this.resolveResourcesEnabled();
        const resourceRows = resourcesEnabled
            ? [
                  { resizer: true, width: 1 },
                  {
                      gravity: 1,
                      config: {
                          columns: nativeResourceColumns,
                          resource_store: 'resource',
                          resource_property: 'owner',
                          row_height: gantt.config.row_height,
                      },
                      cols: [
                          {
                              view: 'resourceGrid',
                              id: 'resourceGrid',
                              bind: 'resource',
                              group: 'grids',
                              width: 260,
                              config: {
                                  columns: nativeResourceColumns,
                                  resource_store: 'resource',
                                  resource_property: 'owner',
                                  row_height: gantt.config.row_height,
                              },
                              scrollY: 'resourceVScroll',
                          },
                          { resizer: true, width: 1 },
                          {
                              view: 'resourceHistogram',
                              bind: 'resource',
                              capacity: 24,
                              scrollX: 'scrollHor',
                              scrollY: 'resourceVScroll',
                          },
                          { view: 'scrollbar', id: 'resourceVScroll', group: 'vertical' },
                      ],
                  },
              ]
            : [];

        gantt.config.layout = {
            css: 'gantt_container',
            rows: [
                {
                    gravity: 2,
                    cols: [
                        { view: 'grid', group: 'grids', scrollY: 'scrollVer' },
                        { resizer: true, width: 1 },
                        { view: 'timeline', scrollX: 'scrollHor', scrollY: 'scrollVer' },
                        { view: 'scrollbar', id: 'scrollVer', group: 'vertical' },
                    ],
                },
                ...resourceRows,
                { view: 'scrollbar', id: 'scrollHor' },
            ],
        };
    }

    private configureTemplates(gantt: GanttStatic): void {
        gantt.templates.timeline_cell_class = (_task, date) => {
            return gantt.isWorkTime(date) ? '' : 'week_end';
        };

        gantt.templates.task_class = (_start, _end, task) => {
            if (this.selectedPathRoot != null && task.id === this.selectedPathRoot) {
                return 'dep-root-task';
            }

            if (this.highlightedTaskIds.has(task.id)) {
                return 'dep-path-task';
            }

            return '';
        };

        gantt.templates.link_class = (link) => {
            return this.highlightedLinkIds.has(link.id) ? 'dep-path-link' : '';
        };

        this.configureResourceHistogramTemplates(gantt);
    }

    private configureResourceHistogramTemplates(gantt: GanttStatic): void {
        const overlaps = (startA: Date, endA: Date, startB: Date, endB: Date) => {
            return startA < endB && startB < endA;
        };

        const hoursBetween = (start: Date, end: Date) => {
            return Math.max(0, (end.getTime() - start.getTime()) / (1000 * 60 * 60));
        };

        const getBookedHours = (task: GanttTask, cellStart: Date, cellEnd: Date): number => {
            if (!task.start_date || !task.end_date) return 0;

            const taskStart = task.start_date as Date;
            const taskEnd = task.end_date as Date;

            if (!overlaps(cellStart, cellEnd, taskStart, taskEnd)) return 0;

            const overlapStart = new Date(Math.max(taskStart.getTime(), cellStart.getTime()));
            const overlapEnd = new Date(Math.min(taskEnd.getTime(), cellEnd.getTime()));

            return hoursBetween(overlapStart, overlapEnd);
        };

        const getTotalBooked = (start: Date, end: Date, resource: MachineResource, tasks: GanttTask[]) => {
            return this.getResourceGraphTasks(resource, tasks).reduce((sum, task) => sum + getBookedHours(task, start, end), 0);
        };

        gantt.templates.histogram_cell_allocated = (start, end, resource, tasks) => {
            return getTotalBooked(start, end, resource, tasks);
        };

        gantt.templates.histogram_cell_label = (start, end, resource, tasks) => {
            const booked = getTotalBooked(start, end, resource, tasks);
            return booked ? `${Math.round(booked * 100) / 100}h` : '';
        };

        gantt.templates.histogram_cell_capacity = (_start, _end, resource) => {
            return Number(resource.capacity ?? 8);
        };

        gantt.templates.histogram_cell_class = (start, end, resource, tasks) => {
            const booked = getTotalBooked(start, end, resource, tasks);
            const capacity = Number(resource.capacity ?? 8);

            if (booked > capacity) return 'hist-overload';
            if (booked === capacity) return 'hist-full';
            return '';
        };
    }

    private configureEvents(gantt: GanttStatic): void {
        gantt.attachEvent('onTaskClick', (id, event) => this.onTaskClick(id, event));
        gantt.attachEvent('onTaskDblClick', (id, event) => this.onTaskDblClick(id, event));
        gantt.attachEvent('onEmptyClick', () => this.onEmptyClick());

        gantt.attachEvent('onBeforeLightbox', () => false);

        gantt.attachEvent('onBeforeLinkAdd', () => this.linksEditable);
        gantt.attachEvent('onBeforeLinkUpdate', () => this.linksEditable);
        gantt.attachEvent('onBeforeLinkDelete', () => this.linksEditable);

        gantt.attachEvent('onAfterAutoSchedule', () => {
            gantt.refreshData();
        });

        gantt.attachEvent('onAfterTaskAutoSchedule', (task, _start, link, predecessor) => {
            this.trackChange({
                event: 'autoschedule',
                dragMode: null,
                entity: 'task',
                id: task.id,
                parentId: task.parent ?? null,
                task,
                link,
                predecessor,
            });
        });

        gantt.attachEvent('onAfterTaskMove', (id, parent) => {
            this.trackChange({
                event: 'move',
                dragMode: null,
                entity: 'task',
                id,
                parentId: parent ?? null,
                task: null,
                link: null,
                predecessor: null,
            });
        });

        gantt.attachEvent('onAfterTaskUpdate', (id, task) => {
            this.trackChange({
                event: 'update',
                dragMode: null,
                entity: 'task',
                id,
                parentId: task.parent ?? null,
                task,
                link: null,
                predecessor: null,
            });
        });

        gantt.attachEvent('onAfterTaskAdd', (id, task) => {
            this.trackChange({
                event: 'add',
                dragMode: null,
                entity: 'task',
                id,
                parentId: task.parent ?? null,
                task,
                link: null,
                predecessor: null,
            });
        });

        gantt.attachEvent('onAfterTaskDelete', (id, task) => {
            this.trackChange({
                event: 'delete',
                dragMode: null,
                entity: 'task',
                id,
                parentId: task.parent ?? null,
                task,
                link: null,
                predecessor: null,
            });
        });

        gantt.attachEvent('onGridResizeEnd', (_oldWidth, newWidth) => {
            if (!this.applyingRememberedState) {
                this.options.config.grid_width = newWidth;
                gantt.config.grid_width = newWidth;
                this.rememberState$.next();
            }

            return true;
        });

        gantt.attachEvent('onColumnResizeEnd', () => {
            if (!this.applyingRememberedState) {
                this.rememberState$.next();
            }

            return true;
        });

        gantt.attachEvent('onGanttRender', () => {
            this.rememberColumnStateIfChanged();
        });

        gantt.attachEvent('onGanttScroll', () => {
            if (!this.applyingRememberedState) {
                this.rememberState$.next();
            }
        });

        gantt.attachEvent('onGanttReady', () => {
            this.ttReady.emit(gantt);
        });
    }

    public onTaskClick(id: string | number, _event?: Event): boolean {
        this.taskClick$.next(id);
        return true;
    }

    public onTaskDblClick(id: string | number, _event?: Event): boolean {
        if (!this.gantt) return false;

        this.taskDblClick$.next(id);

        const task = this.gantt.getTask(id) as GanttTask & Task;
        this.handleTaskEvent(task);

        return true;
    }

    public onEmptyClick(): boolean {
        this.clearDependencyPathHighlight();
        return true;
    }

    private async handleTaskEvent(task: GanttTask & Task): Promise<void> {
        const state = task.item_state;
        const params = task.item_parms;

        if (!state) return;

        if (task.eventtype === 'goto') {
            this.state.go(state, params);
            return;
        }

        if (task.eventtype === 'modal') {
            if (task.component === 'StateModal') {
                await this.modal.openStateDialog({ state, params });
                this.onStateClosed(task);
            }

            return;
        }

        if (task.eventtype === 'popup') {
            const popup = await this.popup.openPopup(`${state}${params ? '/' + params : ''}`, {
                rememberId: `${this.ttRememberId}.${state}.popup`,
            });

            popup?.closed.subscribe({
                next: (closed) => {
                    if (closed) this.onStateClosed(task);
                },
            });
        }
    }

    private onStateClosed(_task: GanttTask & Task): void {
        // Reload data here later if needed.
    }

    private highlightDependencyPath(taskId: string | number): void {
        if (!this.gantt) return;

        const gantt = this.gantt;
        const links = gantt.getLinks();

        this.selectedPathRoot = taskId;
        this.highlightedTaskIds.clear();
        this.highlightedLinkIds.clear();

        const forwardVisited = new Set<string | number>();
        const backwardVisited = new Set<string | number>();

        const walkForward = (currentId: string | number) => {
            if (forwardVisited.has(currentId)) return;
            forwardVisited.add(currentId);

            for (const link of links) {
                if (link.source === currentId) {
                    this.highlightedLinkIds.add(link.id);
                    this.highlightedTaskIds.add(link.target);
                    walkForward(link.target);
                }
            }
        };

        const walkBackward = (currentId: string | number) => {
            if (backwardVisited.has(currentId)) return;
            backwardVisited.add(currentId);

            for (const link of links) {
                if (link.target === currentId) {
                    this.highlightedLinkIds.add(link.id);
                    this.highlightedTaskIds.add(link.source);
                    walkBackward(link.source);
                }
            }
        };

        this.highlightedTaskIds.add(taskId);

        walkForward(taskId);
        walkBackward(taskId);

        gantt.render();
    }

    public clearDependencyPathHighlight(): void {
        if (!this.gantt) return;

        this.selectedPathRoot = null;
        this.highlightedTaskIds.clear();
        this.highlightedLinkIds.clear();

        this.gantt.render();
    }

    private trackChange(change: GanttTaskChangeInfo): void {
        this.changes[String(change.id)] = change;
        this.save$.next();
    }

    public async save() {
        if (!this.gantt) {
            return { errorcode: 0, errormessage: '' };
        }

        const formatDate = this.gantt.date.date_to_str('%Y-%m-%d %H:%i:%s');

        const tasks: GanttTask[] = [];
        this.gantt.eachTask((task: GanttTask) => {
            tasks.push({
                // @ts-ignore
                ...task,
                start_date: task.start_date ? formatDate(task.start_date as Date) : undefined,
                end_date: task.end_date ? formatDate(task.end_date as Date) : undefined,
            });
        });

        const links = this.gantt.getLinks();

        console.log({
            changes: this.changes,
            tasks,
            links,
        });

        return { errorcode: 0, errormessage: '' };
    }

    private async getGanttConfiguration(): Promise<Partial<GanttSetup>> {
        const response = await this.datatask.Post(3130, { tempprocname: 'tt_captasksresources_gantt' }, { returnRawResponse: true });

        return { ...response, data: dataToLoad.data, links: dataToLoad.links, resources: dataToLoad.resources };
    }

    private normalizeTaskDates(tasks: GanttTask[]): GanttTask[] {
        return tasks.map((task) => {
            if (typeof task !== 'object' || task === null) {
                return task;
            }

            return {
                ...task,
                start_date: this.toGanttDate(task['start_date']) ?? task['start_date'],
                end_date: this.toGanttDate(task['end_date']) ?? task['end_date'],
            };
        });
    }

    private resolveResources(resources: MachineResource[] | undefined, tasks: GanttTask[]): MachineResource[] {
        const resolvedResources = resources?.length ? resources : this.buildResourcesFromTasks(tasks);

        return resolvedResources.map((resource) => {
            const namedResource = resource as MachineResource & Record<string, unknown>;

            return {
                ...resource,
                text: String(namedResource['text'] ?? namedResource['label'] ?? namedResource['name'] ?? namedResource['key'] ?? resource.id ?? ''),
            };
        });
    }

    private enableResourcePanelForAvailableResources(resources: MachineResource[]): void {
        if (!resources.length || this.hasExplicitResourceEnabledSetting()) return;

        this.options = {
            ...this.options,
            resources: {
                ...this.options.resources,
                enabled: true,
            },
        };
        this.resourceEnabled = true;
    }

    private hasExplicitResourceEnabledSetting(): boolean {
        return this.hasResourceEnabledSetting(this.backendOptions) || this.hasResourceEnabledSetting(this.ttOptions);
    }

    private hasResourceEnabledSetting(options: PartialGanttOptions | undefined): boolean {
        return !!options?.resources && Object.prototype.hasOwnProperty.call(options.resources, 'enabled');
    }

    private syncResourceStore(gantt: GanttStatic): void {
        if (!this.resources.length) return;

        const store = gantt.getDatastore(gantt.config.resource_store) as any;
        if (!store?.parse) return;

        store.clearAll?.();
        store.parse(this.resources.map((resource) => this.toResourceStoreRow(resource, gantt)));
        store.refresh?.();
    }

    private toResourceStoreRow(resource: MachineResource & Record<string, unknown>, gantt: GanttStatic): MachineResource & Record<string, unknown> {
        const text = this.getResourceDisplayName(resource);

        return {
            ...resource,
            parent: resource['parent'] ?? gantt.config.root_id,
            open: resource['open'] ?? true,
            text,
            label: resource['label'] ?? text,
            name: resource['name'] ?? text,
            resource: resource['resource'] ?? text,
        };
    }

    private normalizeResourceBackedTaskOwners(tasks: GanttTask[], resources: MachineResource[]): GanttTask[] {
        const resourceIds = new Set(resources.map((resource) => String(resource.id)));

        return tasks.map((task) => {
            if (typeof task !== 'object' || task === null || task.id === undefined || !resourceIds.has(String(task.id)) || task['owner'] !== undefined) {
                return task;
            }

            return {
                ...task,
                owner: task.id,
            };
        });
    }

    private buildResourcesFromTasks(tasks: GanttTask[]): MachineResource[] {
        return tasks
            .filter((task) => task['render'] === 'split')
            .map((task) => ({
                id: task.id!,
                text: task.text ?? String(task.id),
                capacity: Number(task.capacity ?? task.capacityHoursPerDay ?? 8),
            }));
    }

    private toGanttGridColumn(column: GanttColumn): DhtmlxGanttConfigOptions['columns'][number] {
        const propertyKey = getColumnPropertyKey(column);

        return {
            name: propertyKey,
            label: column.label ?? propertyKey,
            tree: column.tree ?? propertyKey === 'text',
            width: column.width ?? '*',
            min_width: column.minWidth ?? column['min_width'],
            max_width: column.maxWidth ?? column['max_width'],
            align: column.align,
            resize: column.resize ?? true,
            sort: column.sort,
            template: (task: GanttTask) => {
                const value = task[propertyKey as keyof GanttTask];
                return value == null ? '' : String(value);
            },
        };
    }

    private toGanttResourceGridColumn(column: GanttColumn): DhtmlxGanttConfigOptions['columns'][number] {
        const propertyKey = getColumnPropertyKey(column);

        return {
            ...this.toGanttGridColumn(column),
            template: (resource: MachineResource & Record<string, unknown>) => {
                const value = resource[propertyKey] ?? (this.isResourceNameColumn(propertyKey) ? this.getResourceDisplayName(resource) : undefined);
                return value == null ? '' : String(value);
            },
        };
    }

    private getNativeResourceColumns(): DhtmlxGanttConfigOptions['columns'] {
        const columns = this.getVisibleResourceColumns();

        return columns.map((column) => this.toGanttResourceGridColumn(column));
    }

    private getVisibleResourceColumns(): GanttColumn[] {
        const columns = this.options.resources.columns.map((column) => {
            if (!this.isResourceNameColumn(getColumnPropertyKey(column))) return column;

            return {
                ...column,
                hidden: false,
                hide: false,
                width: this.getUsableResourceColumnWidth(column.width),
                tree: column.tree ?? false,
            };
        });

        const visibleColumns = columns.filter((column) => !this.isColumnHidden(column));

        return visibleColumns.length ? visibleColumns : [this.createResourceNameColumn()];
    }

    private createResourceNameColumn(): GanttColumn {
        return { label: 'Resource', propertyKey: 'text', tree: false, width: '*', resize: true };
    }

    private getUsableResourceColumnWidth(width: GanttColumn['width']): GanttColumn['width'] {
        if (typeof width === 'number' && width < 40) return '*';
        if (typeof width === 'string' && Number(width) < 40) return '*';

        return width ?? '*';
    }

    private isResourceNameColumn(propertyKey: string): boolean {
        return ['text', 'name', 'label', 'resource'].includes(propertyKey);
    }

    private getResourceDisplayName(resource: MachineResource & Record<string, unknown>): string {
        return String(resource['text'] ?? resource['label'] ?? resource['name'] ?? resource['key'] ?? resource.id ?? '');
    }

    private getColumnPropertyKey(column: GanttColumn): string {
        return getColumnPropertyKey(column);
    }

    private isColumnHidden(column: GanttColumn): boolean {
        return customCoerceBooleanProperty(column.hidden ?? column.hide ?? false);
    }

    private inferColumnsFromTasks(tasks: GanttTask[]): GanttColumn[] {
        const firstTask = tasks[0];

        if (!firstTask) {
            return [this.createFallbackTextColumn()];
        }

        const hiddenColumns = new Set(['id', 'parent', 'open', 'render', 'owner', 'capacity', 'capacityHoursPerDay', 'eventtype', 'item_state', 'item_parms', 'component', 'read_on_close', 'split_placement']);

        const columns = Object.keys(firstTask)
            .filter((key) => !key.startsWith('$') && !hiddenColumns.has(key))
            .map((key) => ({
                propertyKey: key,
                label: key === 'text' ? 'Tittel' : key,
                tree: key === 'text',
                width: key === 'text' ? '*' : 110,
                resize: true,
            }));

        return columns.length ? columns : [this.createFallbackTextColumn()];
    }

    private createFallbackTextColumn(): GanttColumn {
        return { label: 'Tittel', propertyKey: 'text', tree: true, width: '*', resize: true };
    }

    private applyNativeConfig(gantt: GanttStatic): void {
        const config = toNativeGanttConfig(this.options);

        Object.assign(gantt.config, config);

        gantt.config['highlight_critical_path'] = this.criticalPath;
        gantt.config['multiselect'] = this.multiSelect;
    }

    private configureAutoSchedulingProjectDates(gantt: GanttStatic, tasks?: GanttTask[]): void {
        gantt.config.project_start = this.toGanttDate(gantt.config.project_start);
        gantt.config.project_end = this.toGanttDate(gantt.config.project_end);

        if (!gantt.config.schedule_from_end || gantt.config.project_end) return;

        const latestEndDate = this.getLatestTaskEndDate(tasks);

        if (latestEndDate) {
            gantt.config.project_end = latestEndDate;
        }
    }

    private getLatestTaskEndDate(tasks?: GanttTask[]): Date | undefined {
        const endDates: Date[] = [];

        if (tasks) {
            tasks.forEach((task) => {
                const date = this.toGanttDate(task.end_date);

                if (date) {
                    endDates.push(date);
                }
            });
        } else if (this.gantt) {
            this.gantt.eachTask((task: GanttTask) => {
                const date = this.toGanttDate(task.end_date);

                if (date) {
                    endDates.push(date);
                }
            });
        }

        return endDates.length ? new Date(Math.max(...endDates.map((date) => date.getTime()))) : undefined;
    }

    private toGanttDate(value: unknown): Date | undefined {
        if (!value) return undefined;
        if (value instanceof Date) return isNaN(value.getTime()) ? undefined : value;

        if (typeof value === 'string') {
            return this.parseGanttDateString(value);
        }

        return undefined;
    }

    private parseGanttDateString(value: string): Date | undefined {
        const trimmed = value.trim();
        if (!trimmed) return undefined;

        const isoLikeDate = trimmed.match(/^(\d{4})-(\d{1,2})-(\d{1,2})(?:[ T](\d{1,2}):(\d{1,2})(?::(\d{1,2}))?)?$/);
        if (isoLikeDate) {
            return this.createValidDate(isoLikeDate[1], isoLikeDate[2], isoLikeDate[3], isoLikeDate[4], isoLikeDate[5], isoLikeDate[6]);
        }

        const dayMonthYearDate = trimmed.match(/^(\d{1,2})-(\d{1,2})-(\d{4})(?:[ T](\d{1,2}):(\d{1,2})(?::(\d{1,2}))?)?$/);
        if (dayMonthYearDate) {
            return this.createValidDate(dayMonthYearDate[3], dayMonthYearDate[2], dayMonthYearDate[1], dayMonthYearDate[4], dayMonthYearDate[5], dayMonthYearDate[6]);
        }

        const date = new Date(trimmed.includes('T') ? trimmed : trimmed.replace(' ', 'T'));

        return isNaN(date.getTime()) ? undefined : date;
    }

    private createValidDate(yearValue: string, monthValue: string, dayValue: string, hourValue = '0', minuteValue = '0', secondValue = '0'): Date | undefined {
        const year = Number(yearValue);
        const month = Number(monthValue);
        const day = Number(dayValue);
        const hour = Number(hourValue);
        const minute = Number(minuteValue);
        const second = Number(secondValue);

        const date = new Date(year, month - 1, day, hour, minute, second);
        const isValid = date.getFullYear() === year && date.getMonth() === month - 1 && date.getDate() === day && date.getHours() === hour && date.getMinutes() === minute && date.getSeconds() === second;

        return isValid ? date : undefined;
    }

    private getResourceGraphTasks(resource: MachineResource, tasks: GanttTask[]): GanttTask[] {
        const tasksById = new Map<string | number, GanttTask>();

        tasks.forEach((task) => {
            if (typeof task === 'object' && task !== null && task.id !== undefined) {
                tasksById.set(task.id, task);
            }
        });

        if (!this.gantt || resource.id === undefined) {
            return Array.from(tasksById.values());
        }

        this.gantt.eachTask((task: GanttTask) => {
            if (typeof task !== 'object' || task === null || task.id === undefined) return;
            if (!this.shouldIncludeProjectInResourceGraph(task, resource.id)) return;

            tasksById.set(task.id, task);
        });

        return Array.from(tasksById.values());
    }

    private shouldIncludeProjectInResourceGraph(task: GanttTask, resourceId: string | number): boolean {
        if (!this.isProjectLikeTask(task) || !this.isTaskAssignedToResource(task, resourceId)) {
            return false;
        }

        return !this.hasAssignedDescendant(task, resourceId);
    }

    private isProjectLikeTask(task: GanttTask): boolean {
        const projectType = this.gantt?.config.types?.project ?? 'project';

        return task['type'] === projectType || task['render'] === 'split' || !!(this.gantt && task.id !== undefined && this.gantt.hasChild(task.id));
    }

    private hasAssignedDescendant(task: GanttTask, resourceId: string | number): boolean {
        if (!this.gantt || task.id === undefined || !this.gantt.hasChild(task.id)) {
            return false;
        }

        let hasAssignedDescendant = false;

        this.gantt.eachTask((descendant: GanttTask) => {
            if (descendant.id !== task.id && this.isTaskAssignedToResource(descendant, resourceId)) {
                hasAssignedDescendant = true;
            }
        }, task.id);

        return hasAssignedDescendant;
    }

    private isTaskAssignedToResource(task: GanttTask, resourceId: string | number): boolean {
        const owner = task['owner'];
        const resourceKey = String(resourceId);
        const getAssignmentResourceId = (assignment: unknown): unknown => (typeof assignment === 'object' && assignment !== null && 'resource_id' in assignment ? assignment['resource_id'] : assignment);

        if (Array.isArray(owner)) {
            return owner.some((assignment) => String(getAssignmentResourceId(assignment)) === resourceKey);
        }

        return String(getAssignmentResourceId(owner)) === resourceKey;
    }

    private resolveCriticalPathEnabled(): boolean {
        return this.options.config['highlight_critical_path'] ?? this.options.config.criticalPath ?? false;
    }

    private resolveMultiSelectEnabled(): boolean {
        return this.options.config['multiselect'] ?? this.options.config.multiSelect ?? false;
    }

    private resolveLinksEditable(): boolean {
        return this.resolveDragOption(this.options.editing.drag.links, this.options.config['drag_links'] ?? this.options.editing.links);
    }

    private resolveResourcesEnabled(): boolean {
        return this.options.resources.enabled;
    }

    private getGanttResourcesConfig(): DhtmlxGanttConfigOptions['resources'] {
        return this.resolveResourcesEnabled() ? (this.options.resources.config ?? true) : false;
    }

    private resolveOptions(preserveRuntimeOptions = false): void {
        this.options = preserveRuntimeOptions ? mergeGanttOptions(this.options, this.backendOptions, this.ttOptions) : mergeGanttOptions(this.backendOptions, this.ttOptions);

        this.criticalPath = this.resolveCriticalPathEnabled();
        this.multiSelect = this.resolveMultiSelectEnabled();
        this.linksEditable = this.resolveLinksEditable();
        this.resourceEnabled = this.resolveResourcesEnabled();
    }

    private applyRememberedState(state: GanttRememberState | undefined, refreshGantt = false): void {
        if (!isGanttRememberState(state)) return;

        this.applyingRememberedState = true;
        this.rememberedState = state;

        this.options.columns = applyRememberedColumnState(this.options.columns, state.columns);
        this.options.resources = {
            ...this.options.resources,
            columns: applyRememberedColumnState(this.options.resources.columns, state.resourceColumns),
        };

        if (state.gridWidth !== undefined) {
            this.options.config.grid_width = state.gridWidth;
        }

        if (refreshGantt && this.gantt) {
            this.configureColumns(this.gantt);
            this.applyNativeConfig(this.gantt);
            this.configureResourceConfig(this.gantt);
            this.configureLayout(this.gantt);
            this.gantt.resetLayout();
            this.syncResourceStore(this.gantt);
            this.applyRememberedZoomLevel(this.gantt, state.zoomLevel);
            this.gantt.render();
            this.restoreRememberedTimelinePosition(this.gantt, state.timelineCenterDate);
        }

        if (this.gantt && !refreshGantt) {
            this.applyRememberedZoomLevel(this.gantt, state.zoomLevel);
            this.restoreRememberedTimelinePosition(this.gantt, state.timelineCenterDate);
        }

        this.lastRememberedStateJson = JSON.stringify(state);
        this.applyingRememberedState = false;
    }

    private applyRememberedZoomLevel(gantt: GanttStatic, zoomLevel: GanttRememberState['zoomLevel']): void {
        if (zoomLevel === undefined) return;

        try {
            gantt.ext.zoom.setLevel(normalizeGanttZoomLevel(zoomLevel, gantt.ext.zoom.getLevels()));
        } catch (error) {
            console.error(error);
        }
    }

    private rememberColumnStateIfChanged(): void {
        if (!this.gantt || this.applyingRememberedState) return;

        const signature = this.getNativeColumnSignature(this.gantt);

        if (!this.lastNativeColumnSignature) {
            this.lastNativeColumnSignature = signature;
            return;
        }

        if (signature !== this.lastNativeColumnSignature) {
            this.lastNativeColumnSignature = signature;
            this.rememberState$.next();
        }
    }

    private getNativeColumnSignature(gantt: GanttStatic): string {
        return JSON.stringify((gantt.config.columns ?? []).map((column) => ({ name: column.name, width: column.width })));
    }

    private getConfiguredColumnsSignature(columns: GanttColumn[]): string {
        return JSON.stringify(columns.map((column) => ({ propertyKey: getColumnPropertyKey(column), width: column.width, hidden: column.hidden ?? column.hide })));
    }

    private rememberCurrentState(): void {
        if (!this.ttRememberId || !this.gantt) return;

        const state = this.captureRememberedState();
        const stateJson = JSON.stringify(state);

        if (stateJson === this.lastRememberedStateJson) return;

        this.rememberedState = state;
        this.lastRememberedStateJson = stateJson;
        this.rememberCurrentStateLocal(state);
        this.remember.remember(this.ttRememberId, state).catch((error) => console.error(error));
    }

    private captureRememberedState(): GanttRememberState {
        this.syncColumnsFromNativeConfig();

        return createGanttRememberState({
            columns: createRememberedColumnState(this.options.columns),
            resourceColumns: createRememberedColumnState(this.options.resources.columns),
            zoomLevel: this.gantt ? getCurrentGanttZoomLevel(this.gantt) : undefined,
            timelineCenterDate: this.captureTimelineCenterDate(),
            gridWidth: typeof this.gantt?.config.grid_width === 'number' ? this.gantt.config.grid_width : undefined,
            sidePanel: this.rememberedState?.sidePanel,
        });
    }

    private syncColumnsFromNativeConfig(): void {
        if (!this.gantt) return;

        this.options.columns = this.mergeNativeColumnStateIntoColumns(this.options.columns, this.gantt.config.columns ?? []);
    }

    private mergeNativeColumnStateIntoColumns(columns: GanttColumn[], nativeColumns: DhtmlxGanttConfigOptions['columns']): GanttColumn[] {
        const columnsByKey = new Map(columns.map((column) => [getColumnPropertyKey(column), column]));
        const nativeColumnKeys = nativeColumns.map((column) => column.name).filter((name): name is string => !!name);
        const orderedVisibleColumns = nativeColumnKeys.reduce<GanttColumn[]>((result, propertyKey) => {
            const column = columnsByKey.get(propertyKey);
            const nativeColumn = nativeColumns.find((item) => item.name === propertyKey);

            if (column) {
                result.push({
                    ...column,
                    width: nativeColumn?.width ?? column.width,
                });
            }

            return result;
        }, []);

        const visibleColumnKeys = new Set(orderedVisibleColumns.map((column) => getColumnPropertyKey(column)));
        const remainingColumns = columns.filter((column) => !visibleColumnKeys.has(getColumnPropertyKey(column)));

        return [...orderedVisibleColumns, ...remainingColumns];
    }

    private captureTimelineCenterDate(): string | undefined {
        if (!this.gantt) return undefined;

        try {
            const scrollState = this.gantt.getScrollState();
            const timelineWidth = this.gantt.$task?.clientWidth ?? 0;
            const centerPosition = (scrollState?.x ?? 0) + timelineWidth / 2;
            const centerDate = this.gantt.dateFromPos(centerPosition);

            return centerDate instanceof Date && !isNaN(centerDate.getTime()) ? centerDate.toISOString() : undefined;
        } catch (error) {
            console.error(error);
            return undefined;
        }
    }

    private restoreRememberedTimelinePosition(gantt: GanttStatic, timelineCenterDate: string | undefined): void {
        if (!timelineCenterDate) return;

        try {
            const date = new Date(timelineCenterDate);

            if (isNaN(date.getTime())) return;

            const timelineWidth = gantt.$task?.clientWidth ?? 0;
            const scrollLeft = Math.max(0, gantt.posFromDate(date) - timelineWidth / 2);

            gantt.scrollTo(scrollLeft, null);
        } catch (error) {
            console.error(error);
        }
    }

    private getLastRememberedStateLocal(): GanttRememberState | undefined {
        if (!this.ttRememberId) return undefined;

        const localStorageKey = this.getLocalStorageKey();
        if (!localStorageKey) return undefined;

        try {
            const allStates = JSON.parse(localStorage.getItem(localStorageKey) || '{}');
            const state = allStates[this.ttRememberId];

            return isGanttRememberState(state) ? state : undefined;
        } catch (error) {
            console.error(error);
            return undefined;
        }
    }

    private rememberCurrentStateLocal(state: GanttRememberState): void {
        const localStorageKey = this.getLocalStorageKey();
        if (!localStorageKey) return;

        try {
            const allStates = JSON.parse(localStorage.getItem(localStorageKey) || '{}');
            allStates[this.ttRememberId] = state;
            localStorage.setItem(localStorageKey, JSON.stringify(allStates));
        } catch (error) {
            console.error(error);
        }
    }

    private async getLastRememberedState(): Promise<GanttRememberState | undefined> {
        if (!this.ttRememberId) return undefined;

        try {
            const state = await this.remember.getLastStatus(this.ttRememberId, true);

            return isGanttRememberState(state) ? state : undefined;
        } catch (error) {
            console.error(error);
            return undefined;
        }
    }

    private getLocalStorageKey(): string | undefined {
        const userKey = this.appSettings.settings.user?.portalUserKeynoEdit;

        return userKey ? `tt-gantt.${userKey}` : undefined;
    }
}

const data: GanttTask[] = [
    {
        id: 'machine-cnc-01',
        text: 'CNC-01',
        render: 'split',
        open: true,
        start_date: '14-04-2026 08:00',
        end_date: '16-04-2026 16:00',
        machineType: 'CNC',
        capacityHoursPerDay: 16,
        eventtype: 'modal',
        component: 'StateModal',
        item_state: 'project',
        item_parms: '10500',
        read_on_close: 1,
    },
    {
        id: 'job-1001',
        text: 'JOB-1001 - Pump Housing',
        parent: 'machine-cnc-01',
        owner: 'machine-cnc-01',
        start_date: '14-04-2026 08:00',
        end_date: '14-04-2026 14:00',
        split_placement: 'inline',
        orderNo: '1001',
        customer: 'NorthSteel',
        workloadHours: 6,
        status: 'planned',
    },
    {
        id: 'job-1002',
        text: 'JOB-1002 - Valve Block',
        parent: 'machine-cnc-01',
        owner: 'machine-cnc-01',
        start_date: '14-04-2026 14:30',
        end_date: '15-04-2026 10:30',
        split_placement: 'inline',
        orderNo: '1002',
        customer: 'HydroWorks',
        workloadHours: 20,
        status: 'planned',
    },
    {
        id: 'job-1003',
        text: 'JOB-1003 - Bearing Plate',
        parent: 'machine-cnc-01',
        owner: 'machine-cnc-01',
        start_date: '15-04-2026 11:00',
        end_date: '16-04-2026 16:00',
        split_placement: 'inline',
        orderNo: '1003',
        customer: 'ScanMach',
        workloadHours: 29,
        status: 'planned',
    },

    {
        id: 'machine-cnc-02',
        text: 'CNC-02',
        render: 'split',
        open: true,
        start_date: '14-04-2026 08:00',
        end_date: '17-04-2026 15:00',
        machineType: 'CNC',
        capacityHoursPerDay: 16,
    },
    {
        id: 'job-2001',
        text: 'JOB-2001 - Flange Set',
        parent: 'machine-cnc-02',
        owner: 'machine-cnc-02',
        start_date: '14-04-2026 08:00',
        end_date: '14-04-2026 15:00',
        split_placement: 'inline',
        orderNo: '2001',
        customer: 'PipeCore',
        workloadHours: 7,
        status: 'planned',
    },
    {
        id: 'job-2002',
        text: 'JOB-2002 - Mounting Bracket',
        parent: 'machine-cnc-02',
        owner: 'machine-cnc-02',
        start_date: '14-04-2026 15:30',
        end_date: '15-04-2026 12:30',
        split_placement: 'inline',
        orderNo: '2002',
        customer: 'LiftSys',
        workloadHours: 21,
        status: 'planned',
    },
    {
        id: 'job-2003',
        text: 'JOB-2003 - Shaft Collar',
        parent: 'machine-cnc-02',
        owner: 'machine-cnc-02',
        start_date: '15-04-2026 13:00',
        end_date: '15-04-2026 19:00',
        split_placement: 'inline',
        orderNo: '2003',
        customer: 'NordDrive',
        workloadHours: 6,
        status: 'planned',
    },
    {
        id: 'job-2004',
        text: 'JOB-2004 - Adapter Ring',
        parent: 'machine-cnc-02',
        owner: 'machine-cnc-02',
        start_date: '16-04-2026 08:00',
        end_date: '17-04-2026 15:00',
        split_placement: 'inline',
        orderNo: '2004',
        customer: 'NordDrive',
        workloadHours: 31,
        status: 'planned',
    },

    {
        id: 'machine-laser-01',
        text: 'LASER-01',
        render: 'split',
        open: true,
        start_date: '14-04-2026 07:00',
        end_date: '15-04-2026 18:30',
        machineType: 'Laser',
        capacityHoursPerDay: 12,
    },
    {
        id: 'job-3001',
        text: 'JOB-3001 - Sheet Set A',
        parent: 'machine-laser-01',
        owner: 'machine-laser-01',
        start_date: '14-04-2026 07:00',
        end_date: '14-04-2026 12:00',
        split_placement: 'inline',
        orderNo: '3001',
        material: 'SS304',
        workloadHours: 5,
        status: 'planned',
    },
    {
        id: 'job-3002',
        text: 'JOB-3002 - Panel Kit',
        parent: 'machine-laser-01',
        owner: 'machine-laser-01',
        start_date: '14-04-2026 12:30',
        end_date: '15-04-2026 10:30',
        split_placement: 'inline',
        orderNo: '3002',
        material: 'Mild Steel',
        workloadHours: 22,
        status: 'planned',
    },
    {
        id: 'job-3003',
        text: 'JOB-3003 - Cover Plates',
        parent: 'machine-laser-01',
        owner: 'machine-laser-01',
        start_date: '15-04-2026 11:00',
        end_date: '15-04-2026 15:00',
        split_placement: 'inline',
        orderNo: '3003',
        material: 'Aluminium',
        workloadHours: 4,
        status: 'planned',
    },
    {
        id: 'job-3004',
        text: 'JOB-3004 - Guard Frames',
        parent: 'machine-laser-01',
        owner: 'machine-laser-01',
        start_date: '15-04-2026 15:30',
        end_date: '15-04-2026 18:30',
        split_placement: 'inline',
        orderNo: '3004',
        material: 'Mild Steel',
        workloadHours: 3,
        status: 'planned',
    },

    {
        id: 'machine-press-01',
        text: 'PRESS-01',
        render: 'split',
        open: true,
        start_date: '16-04-2026 08:00',
        end_date: '17-04-2026 13:00',
        machineType: 'Press Brake',
        capacityHoursPerDay: 8,
    },
    {
        id: 'job-4001',
        text: 'JOB-4001 - Bracket Forming',
        parent: 'machine-press-01',
        owner: 'machine-press-01',
        start_date: '16-04-2026 08:00',
        end_date: '16-04-2026 15:00',
        split_placement: 'inline',
        orderNo: '4001',
        workloadHours: 7,
        status: 'planned',
    },
    {
        id: 'job-4002',
        text: 'JOB-4002 - Chassis Bends',
        parent: 'machine-press-01',
        owner: 'machine-press-01',
        start_date: '16-04-2026 15:30',
        end_date: '17-04-2026 09:30',
        split_placement: 'inline',
        orderNo: '4002',
        workloadHours: 18,
        status: 'planned',
    },
    {
        id: 'job-4003',
        text: 'JOB-4003 - Tray Batch',
        parent: 'machine-press-01',
        owner: 'machine-press-01',
        start_date: '17-04-2026 10:00',
        end_date: '17-04-2026 13:00',
        split_placement: 'inline',
        orderNo: '4003',
        workloadHours: 3,
        status: 'planned',
    },

    {
        id: 'machine-paint-01',
        text: 'PAINT-01',
        render: 'split',
        open: true,
        start_date: '17-04-2026 08:00',
        end_date: '20-04-2026 13:00',
        machineType: 'Powder Coat',
        capacityHoursPerDay: 8,
    },
    {
        id: 'job-5001',
        text: 'JOB-5001 - Blue Batch',
        parent: 'machine-paint-01',
        owner: 'machine-paint-01',
        start_date: '17-04-2026 08:00',
        end_date: '17-04-2026 12:00',
        split_placement: 'inline',
        orderNo: '5001',
        color: '#4a90e2',
        workloadHours: 4,
        status: 'planned',
    },
    {
        id: 'job-5002',
        text: 'JOB-5002 - Black Batch',
        parent: 'machine-paint-01',
        owner: 'machine-paint-01',
        start_date: '17-04-2026 12:30',
        end_date: '18-04-2026 12:30',
        split_placement: 'inline',
        orderNo: '5002',
        // color: '#333333',
        workloadHours: 24,
        status: 'planned',
    },
    {
        id: 'job-5003',
        text: 'JOB-5003 - Safety Yellow Batch',
        parent: 'machine-paint-01',
        owner: 'machine-paint-01',
        start_date: '20-04-2026 08:00',
        end_date: '20-04-2026 13:00',
        split_placement: 'inline',
        orderNo: '5003',
        // color: '#f5c542',
        workloadHours: 5,
        status: 'planned',
    },
];

const links: Link[] = [
    { id: 'l-1001', source: 'job-1001', target: 'job-1002', type: '0' },
    { id: 'l-1002', source: 'job-1002', target: 'job-1003', type: '0' },

    { id: 'l-2001', source: 'job-2001', target: 'job-2002', type: '0' },
    { id: 'l-2002', source: 'job-2002', target: 'job-2003', type: '0' },
    { id: 'l-2003', source: 'job-2003', target: 'job-2004', type: '0' },

    { id: 'l-3001', source: 'job-3001', target: 'job-3002', type: '0' },
    { id: 'l-3002', source: 'job-3002', target: 'job-3003', type: '0' },
    { id: 'l-3003', source: 'job-3003', target: 'job-3004', type: '0' },

    { id: 'l-4001', source: 'job-4001', target: 'job-4002', type: '0' },
    { id: 'l-4002', source: 'job-4002', target: 'job-4003', type: '0' },

    { id: 'l-5001', source: 'job-5001', target: 'job-5002', type: '0' },
    { id: 'l-5002', source: 'job-5002', target: 'job-5003', type: '0' },

    { id: 'l-x1', source: 'job-3002', target: 'job-1002', type: '0' },
    { id: 'l-x2', source: 'job-1003', target: 'job-4002', type: '0' },
    { id: 'l-x3', source: 'job-4003', target: 'job-5002', type: '0' },
    { id: 'l-x4', source: 'job-2004', target: 'job-5003', type: '0' },
];

const resources: MachineResource[] = [
    { id: 'machine-cnc-01', text: 'CNC-01', capacity: 16 },
    { id: 'machine-cnc-02', text: 'CNC-02', capacity: 16 },
    { id: 'machine-laser-01', text: 'LASER-01', capacity: 12 },
    { id: 'machine-press-01', text: 'PRESS-01', capacity: 8 },
    { id: 'machine-paint-01', text: 'PAINT-01', capacity: 8 },
];

const dataToLoad = {
    data,
    links,
    resources,
};
